import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Pencil, Trash2, Building2, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { getAllFirms, createFirm, updateFirm, deleteFirm, uploadTaskFile } from '../../services/taskApi';
import { toast } from 'sonner';

const Firms = () => {
  const { isDark } = useOutletContext();
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '',
    gst_number: '', pan_number: '', phone: '', email: '',
    bank_name: '', bank_account: '', bank_ifsc: '', is_active: true, logo: ''
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const getImgUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_BASE}${url}`);

  const cardCls = isDark ? 'border-0 bg-white/5' : 'border border-gray-200 bg-white shadow-sm';
  const dialogCls = isDark ? 'bg-[#0c1222] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900';
  const labelCls = isDark ? 'text-white/70' : 'text-gray-600';
  const btnOutlineCls = isDark ? 'border-white/10 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100';

  const fetchData = async () => {
    try { const res = await getAllFirms(); setFirms(res.data || []); } catch (err) { console.error('Firms: getAllFirms failed', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '', gst_number: '', pan_number: '', phone: '', email: '', bank_name: '', bank_account: '', bank_ifsc: '', is_active: true, logo: '' });
    setDialogOpen(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setForm({
      name: f.name || '', address_line1: f.address_line1 || '', address_line2: f.address_line2 || '',
      city: f.city || '', state: f.state || '', pincode: f.pincode || '',
      gst_number: f.gst_number || '', pan_number: f.pan_number || '',
      phone: f.phone || '', email: f.email || '',
      bank_name: f.bank_name || '', bank_account: f.bank_account || '', bank_ifsc: f.bank_ifsc || '',
      is_active: f.is_active !== false, logo: f.logo || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await updateFirm(editing.id, form); toast.success('Firm updated'); }
      else { await createFirm(form); toast.success('Firm created'); }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this firm?')) return;
    try { await deleteFirm(id); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="firms-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Firm Master</h1>
          <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{firms.length} firms configured</p>
        </div>
        <Button size="sm" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-firm-btn"><Plus size={14} className="mr-1" /> Add Firm</Button>
      </div>

      {loading ? <div className="space-y-3">{[...Array(2)].map((_, i) => <div key={i} className={`rounded-lg h-28 animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div> : firms.length === 0 ? (
        <div className={`text-center py-16 ${isDark ? 'text-white/30' : 'text-gray-400'}`}><Building2 size={40} className="mx-auto mb-3 opacity-30" /><p>No firms configured</p></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {firms.map(f => (
            <Card key={f.id} className={`${cardCls} ${!f.is_active ? 'opacity-50' : ''}`} data-testid={`firm-card-${f.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {f.logo && <img src={getImgUrl(f.logo)} alt="" className={`w-12 h-12 rounded-lg object-contain border ${isDark ? 'border-white/10' : 'border-gray-200'}`} />}
                    <div>
                      <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{f.name}</h3>
                      {!f.is_active && <span className="text-[10px] bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full">Inactive</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${isDark ? 'text-white/40 hover:bg-white/5' : 'text-gray-400 hover:bg-gray-100'}`} onClick={() => openEdit(f)}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-7 w-7 p-0" onClick={() => handleDelete(f.id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
                <div className={`text-xs space-y-0.5 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  {f.address_line1 && <p>{f.address_line1}</p>}
                  {f.city && <p>{f.city}, {f.state} - {f.pincode}</p>}
                  {f.phone && <p>Phone: {f.phone}</p>}
                  {f.email && <p>Email: {f.email}</p>}
                  {f.gst_number && <p className={`font-mono ${isDark ? 'text-white/60' : 'text-gray-700'}`}>GST: {f.gst_number}</p>}
                  {f.pan_number && <p className={`font-mono ${isDark ? 'text-white/60' : 'text-gray-700'}`}>PAN: {f.pan_number}</p>}
                </div>
                {f.bank_name && (
                  <div className={`text-[10px] border-t pt-2 ${isDark ? 'text-white/30 border-white/5' : 'text-gray-400 border-gray-200'}`}>
                    Bank: {f.bank_name} | A/C: {f.bank_account} | IFSC: {f.bank_ifsc}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`max-w-lg max-h-[90vh] overflow-y-auto ${dialogCls}`}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>{editing ? 'Edit' : 'Add'} Firm</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className={labelCls}>Firm Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className={inputCls} data-testid="firm-name-input" /></div>
            <div><Label className={labelCls}>Address Line 1</Label><Input value={form.address_line1} onChange={e => setForm({ ...form, address_line1: e.target.value })} className={inputCls} /></div>
            <div><Label className={labelCls}>Address Line 2</Label><Input value={form.address_line2} onChange={e => setForm({ ...form, address_line2: e.target.value })} className={inputCls} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className={labelCls}>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className={inputCls} /></div>
              <div><Label className={labelCls}>State</Label><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className={inputCls} /></div>
              <div><Label className={labelCls}>Pincode</Label><Input value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={labelCls}>GST Number</Label><Input value={form.gst_number} onChange={e => setForm({ ...form, gst_number: e.target.value })} className={inputCls} data-testid="firm-gst-input" /></div>
              <div><Label className={labelCls}>PAN Number</Label><Input value={form.pan_number} onChange={e => setForm({ ...form, pan_number: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={labelCls}>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
              <div><Label className={labelCls}>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
            </div>
            <h4 className={`text-sm font-medium pt-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Bank Details (for quotation PDF)</h4>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className={labelCls}>Bank Name</Label><Input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} className={inputCls} /></div>
              <div><Label className={labelCls}>Account No.</Label><Input value={form.bank_account} onChange={e => setForm({ ...form, bank_account: e.target.value })} className={inputCls} /></div>
              <div><Label className={labelCls}>IFSC Code</Label><Input value={form.bank_ifsc} onChange={e => setForm({ ...form, bank_ifsc: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} /><Label className={isDark ? 'text-white/60' : 'text-gray-600'}>Active</Label></div>
            <div>
              <Label className={labelCls}>Company Logo</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.logo && <img src={getImgUrl(form.logo)} alt="" className={`w-16 h-16 object-contain rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`} />}
                <label className="cursor-pointer">
                  <div className={`border-2 border-dashed rounded-lg px-4 py-3 text-center transition-colors ${
                    isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-300 hover:bg-gray-50'
                  }`}>
                    <Upload size={18} className={`mx-auto mb-1 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
                    <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{logoUploading ? 'Uploading...' : 'Upload Logo'}</p>
                  </div>
                  <input type="file" accept="image/*" className="hidden" disabled={logoUploading} onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    setLogoUploading(true);
                    try { const res = await uploadTaskFile(file); setForm(f => ({ ...f, logo: res.data.url })); toast.success('Logo uploaded'); }
                    catch { toast.error('Upload failed'); }
                    finally { setLogoUploading(false); }
                  }} />
                </label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="firm-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className={btnOutlineCls}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Firms;
